import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress } from "@material-ui/core";
import RefreshRoundedIcon from "@material-ui/icons/RefreshRounded";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

/**
 * Core engine — the owner's window onto what the Brain knows about our OWN
 * engineering estate (Tenant Zero), read from GET /ai/brain/core/portal.
 *
 * The design carries the Core's own rule: honest states beat fabricated figures.
 * So no row is ever hidden, greyed out or collapsed for having no data — an area
 * that cannot speak still reports WHY and WHICH core would have answered, because
 * "we can't see this, and here is precisely what is missing" is real information
 * about our own wiring. The four states are visually distinct on purpose:
 *   ok            we asked and there are figures
 *   empty         we asked; there is genuinely nothing to report (not a fault)
 *   insufficient  we could not ask — a source or producer is missing
 *   error         a core threw; only its error NAME is ever surfaced
 * Collapsing empty and insufficient into one "no data" look would erase the most
 * useful distinction on the page.
 */

const STATE_COLOR = {
  ok: "#2ee37a",
  empty: "#6f757e",
  insufficient: "#e0a92e",
  error: "#e35b5b",
};

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(1, 0) },

  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(3),
    // v4 makeStyles: the media query MUST sit inside the rule. Declared at the
    // top level of the styles object it would emit `@media { header { … } }` —
    // a tag selector that matches nothing here, so the rule would silently die.
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      marginBottom: theme.spacing(2),
    },
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 500,
    letterSpacing: "-0.01em",
    color: "#f4f5f6",
  },
  subtitle: {
    margin: theme.spacing(0.75, 0, 0),
    fontSize: 13,
    lineHeight: 1.5,
    color: "#a8acb3",
    maxWidth: 620,
  },

  /* Coverage counter — the one figure that frames everything below it. */
  coverage: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(1),
    fontVariantNumeric: "tabular-nums",
  },
  coverageNum: { fontSize: 28, fontWeight: 500, color: "#f4f5f6", lineHeight: 1 },
  coverageLabel: { fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "#a8acb3" },

  list: { display: "flex", flexDirection: "column", gap: theme.spacing(1) },

  row: {
    display: "flex",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: "#1A1E1B",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 6,
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(1.25, 1.5) },
  },
  /* The state is readable before any text is parsed. */
  bar: { width: 3, borderRadius: 2, flexShrink: 0, alignSelf: "stretch" },
  body: { minWidth: 0, flex: 1 },

  rowTop: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  area: { fontSize: 14, fontWeight: 500, color: "#f4f5f6" },
  state: { fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase" },

  reason: { marginTop: 4, fontSize: 13, color: "#a8acb3", wordBreak: "break-word" },

  /* Provenance: which core produced (or would have produced) this. */
  source: {
    marginTop: 6,
    fontSize: 11,
    letterSpacing: "0.04em",
    color: "#6f757e",
    wordBreak: "break-all",
  },

  figures: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: "1px solid rgba(255,255,255,0.06)",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75, 2),
  },
  figure: { fontSize: 12, color: "#c9ced6", fontVariantNumeric: "tabular-nums" },
  figureKey: { color: "#6f757e", marginRight: 4 },

  notice: {
    padding: theme.spacing(2),
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 6,
    backgroundColor: "#1A1E1B",
    color: "#a8acb3",
    fontSize: 13,
    lineHeight: 1.6,
  },
  center: { display: "flex", justifyContent: "center", padding: theme.spacing(6, 0) },

  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { width: "100%", justifyContent: "space-between" },
  },
}));

/** Flatten an area's payload into label/value pairs, refs and counts only. */
const toFigures = data => {
  if (!data || typeof data !== "object") return [];
  return Object.keys(data)
    .map(key => {
      const value = data[key];
      if (value === null || value === undefined) return null;
      if (Array.isArray(value)) return { key, value: `${value.length}` };
      if (typeof value === "object") return null; // nested shapes stay collapsed
      return { key, value: String(value) };
    })
    .filter(Boolean)
    .slice(0, 8);
};

const CoreEnginePanel = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [envelope, setEnvelope] = useState(null);
  const [failure, setFailure] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    try {
      const { data } = await api.get("/ai/brain/core/portal");
      setEnvelope(data);
    } catch (err) {
      const code =
        (err && err.response && err.response.data && err.response.data.error) || "unreachable";
      setFailure(code);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const areas = (envelope && envelope.areas) || [];
  const withData = areas.filter(a => a.state === "ok").length;

  const renderNotice = text => <div className={classes.notice}>{text}</div>;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <h2 className={classes.title}>
            {i18n.t("admin.core.title", { defaultValue: "Core engine" })}
          </h2>
          <p className={classes.subtitle}>
            {i18n.t("admin.core.subtitle", {
              defaultValue:
                "What the Brain currently knows about our own codebase — and where it cannot see yet. Every area reports either real figures or the exact reason it has none.",
            })}
          </p>
        </div>

        <div className={classes.actions}>
          {envelope && envelope.enabled && (
            <div className={classes.coverage}>
              <span className={classes.coverageNum}>
                {withData}/{areas.length}
              </span>
              <span className={classes.coverageLabel}>
                {i18n.t("admin.core.reporting", { defaultValue: "reporting" })}
              </span>
            </div>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={load}
            disabled={loading}
            startIcon={<RefreshRoundedIcon />}
          >
            {i18n.t("admin.core.refresh", { defaultValue: "Refresh" })}
          </Button>
        </div>
      </div>

      {loading && (
        <div className={classes.center}>
          <CircularProgress size={26} />
        </div>
      )}

      {!loading &&
        failure === "core-not-provisioned" &&
        renderNotice(
          i18n.t("admin.core.notProvisioned", {
            defaultValue:
              "Tenant Zero is not configured on this environment, so the Core has nothing to run under. Set AI_CORE_TENANT_ZERO_COMPANY_ID and restart the backend.",
          })
        )}

      {!loading &&
        failure &&
        failure !== "core-not-provisioned" &&
        renderNotice(
          i18n.t("admin.core.unreachable", {
            defaultValue: "The Core portal could not be read. Check the backend is running, then refresh.",
          })
        )}

      {!loading && !failure && envelope && !envelope.enabled &&
        renderNotice(
          i18n.t("admin.core.gateClosed", {
            defaultValue:
              "The Core portal is switched off, so nothing was read and no core was consulted. It needs both keys: AI_CORE_SCANNER=true in the backend environment and the matching Tenant Zero opt-in row.",
          })
        )}

      {!loading && !failure && envelope && envelope.enabled && (
        <div className={classes.list}>
          {areas.map(area => {
            const color = STATE_COLOR[area.state] || STATE_COLOR.empty;
            const prov = area.provenance || {};
            const figures = area.state === "ok" ? toFigures(area.data) : [];
            return (
              <div key={area.area} className={classes.row}>
                <div className={classes.bar} style={{ backgroundColor: color }} />
                <div className={classes.body}>
                  <div className={classes.rowTop}>
                    <span className={classes.area}>{area.area}</span>
                    <span className={classes.state} style={{ color }}>
                      {area.state}
                    </span>
                  </div>

                  {prov.reason && <div className={classes.reason}>{prov.reason}</div>}

                  {prov.sourceCore && (
                    <div className={classes.source}>
                      {prov.sourceCore}
                      {prov.truthClass ? ` · ${prov.truthClass}` : ""}
                    </div>
                  )}

                  {figures.length > 0 && (
                    <div className={classes.figures}>
                      {figures.map(f => (
                        <span key={f.key} className={classes.figure}>
                          <span className={classes.figureKey}>{f.key}</span>
                          {f.value}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoreEnginePanel;
